import {
  trackClickedProductPageTab,
  trackViewedProductPage
} from '@/services/analytics'
import { getQuantity } from '@/catalog/product-options'
import $ from 'jquery'
import { TapEventHandler } from 'bootstrap'
import { RequestProductQuoteProperties } from '@/analytics/types'
import { ProductPageItem } from '@/globals'
import { Item } from '@/api/items'

// Inlined item will always exist via product.jade
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const item = window.item!

/**
 * Builds an object for analytics context to enrich all product page events with standardized item data.
 */
const getCommonAnalyticsTrackingProperties = (
  itemAttributes: Item | ProductPageItem = item
) => {
  const canonicalCategory = itemAttributes.canonicalCategory
  return {
    item: {
      id: itemAttributes.id,
      code: itemAttributes.code,
      pageTitle: itemAttributes.pageTitle,
      price: itemAttributes.prices[itemAttributes.prices.length - 1]!,
      designerName: itemAttributes.designerName,
      canonicalColor: itemAttributes.canonicalColor,
      canonicalCategory: {
        minorCategory: canonicalCategory.minorCategory,
        majorCategory: canonicalCategory.majorCategory
      }
    }
  }
}

/**
 * Builds an object for analytics context to enrich product page events for the main item with standardized item data.
 */
export const getAnalyticsTrackingProperties = (
  itemAttributes: Item | ProductPageItem = item
) => {
  return {
    item: {
      ...getCommonAnalyticsTrackingProperties(itemAttributes).item,
      quantity: getQuantity()
    }
  }
}

/**
 * Product page context for requesting a quote.
 * Does not contain `quantity` because its value is irrelevant when the quote is being requested
 * (before the form is completed).
 *
 * This is not defined in `analytics.ts` to decouple this and its dependencies from `analytics.ts`.
 * In turn, there is no circular dependency, other pages won't depend on product-specific modules,
 * and product logic (especially where `window.item` is assumed to be defined) won't execute on other pages.
 */
export const getRequestProductQuoteProperties = (
  itemAttributes: Item | ProductPageItem
): RequestProductQuoteProperties =>
  getCommonAnalyticsTrackingProperties(itemAttributes)

const trackPageview = () => {
  const canonicalCategory = item.canonicalCategory
  trackViewedProductPage({
    id: item.id,
    code: item.code,
    pageTitle: item.pageTitle,
    /** Unit price (cost per qty). */
    // There is always at least once price per item
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    price: item.prices[item.prices.length - 1]!,
    // Don't send the actual quantity to avoid skewed analyses based on default quantities (without user intention)
    quantity: 1,
    canonicalColor: item.canonicalColor,
    designerName: item.designerName,
    canonicalCategory: {
      majorCategory: canonicalCategory.majorCategory,
      minorCategory: canonicalCategory.minorCategory
    }
  })
}

const trackInfoTabClick = (e: JQuery.ClickEvent | TapEventHandler) => {
  trackClickedProductPageTab({
    ...getAnalyticsTrackingProperties(),
    title: $(e.target).text().trim()
  })
}

const initializeTabTracking = () => {
  $('a[data-toggle="tab"]').on('shown.bs.tab', trackInfoTabClick)
  $('#prod-info .collapse-container a.section-title').on(
    'click',
    trackInfoTabClick
  )
}

export const initializeAnalytics = () => {
  trackPageview()
  initializeTabTracking()
}
